import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BookOpen, Languages, MessageCircle, PauseCircle, History as HistoryIcon, NotebookPen, Cpu, Menu, X } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/17643586/pexels-photo-17643586.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=800";
const ABOUT_IMG = "https://images.unsplash.com/photo-1696685969275-dd7f8cf52d5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const open = () => navigate("/study");

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  const goTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const Cta = ({ className = "", label = "Open the Word" }) => (
    <Button
      data-testid="open-study-cta"
      onClick={open}
      className={`h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-8 text-base font-medium ${className}`}
    >
      {label}
    </Button>
  );

  const Verse = ({ children, cite }) => (
    <figure className="mt-6">
      <blockquote className="text-xl sm:text-2xl italic text-slate-200 leading-relaxed">
        {children}
      </blockquote>
      <figcaption className="text-sm text-[#00E5FF] mt-3">— {cite}</figcaption>
    </figure>
  );

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-[#07090E]/85 backdrop-blur-xl border-b border-[#1E273D]">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2">
            <img src="/openword-logo-horizontal.png" alt="OpenWord AI" className="h-6" />
          </button>
          <div className="hidden md:flex items-center gap-7 text-sm text-slate-400">
            <button onClick={() => scrollTo("how")} className="hover:text-[#00E5FF] transition-colors">How It Works</button>
            <button onClick={() => scrollTo("about")} className="hover:text-[#00E5FF] transition-colors">About</button>
            <button onClick={() => scrollTo("resources")} className="hover:text-[#00E5FF] transition-colors">Resources</button>
            <button onClick={() => navigate("/blog")} data-testid="nav-reflections-link" className="hover:text-[#00E5FF] transition-colors">Reflections</button>
            <button onClick={() => navigate("/pricing")} className="hover:text-[#00E5FF] transition-colors">Pricing</button>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={open} data-testid="nav-open-study" variant="ghost" className="hidden sm:inline-flex rounded-full text-[#00E5FF] hover:bg-[#10222A] h-10 px-5">
              Open the Word
            </Button>
            <Button onClick={open} data-testid="nav-open-study-mobile" variant="ghost" className="sm:hidden rounded-full text-[#00E5FF] hover:bg-[#10222A] h-9 px-3 text-sm">
              Start
            </Button>
            <button
              data-testid="mobile-menu-open"
              onClick={() => setMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-slate-300 hover:text-[#00E5FF] hover:bg-[#10222A] transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile nav sheet */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="bg-[#0B0F16] border-l border-[#1E273D] w-[78vw] max-w-sm p-0">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Site navigation</SheetDescription>
          <div className="flex items-center justify-between px-5 h-16 border-b border-[#1E273D]">
            <img src="/openword-logo-horizontal.png" alt="OpenWord AI" className="h-6" />
            <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="text-slate-400 hover:text-[#00E5FF]">
              <X className="w-6 h-6" strokeWidth={1.75} />
            </button>
          </div>
          <nav className="flex flex-col p-5 gap-1 text-base text-slate-300">
            <button data-testid="mobile-nav-how" onClick={() => scrollTo("how")} className="text-left py-3 border-b border-[#1E273D] hover:text-[#00E5FF] transition-colors">How It Works</button>
            <button data-testid="mobile-nav-about" onClick={() => scrollTo("about")} className="text-left py-3 border-b border-[#1E273D] hover:text-[#00E5FF] transition-colors">About</button>
            <button data-testid="mobile-nav-resources" onClick={() => scrollTo("resources")} className="text-left py-3 border-b border-[#1E273D] hover:text-[#00E5FF] transition-colors">Resources</button>
            <button data-testid="mobile-nav-reflections" onClick={() => goTo("/blog")} className="text-left py-3 border-b border-[#1E273D] hover:text-[#00E5FF] transition-colors">Reflections</button>
            <button data-testid="mobile-nav-pricing" onClick={() => goTo("/pricing")} className="text-left py-3 border-b border-[#1E273D] hover:text-[#00E5FF] transition-colors">Pricing</button>
            <button data-testid="mobile-nav-login" onClick={() => goTo("/login")} className="text-left py-3 hover:text-[#00E5FF] transition-colors">Log in</button>
          </nav>
          <div className="p-5">
            <Button onClick={open} data-testid="mobile-nav-cta" className="w-full h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-base font-medium">
              Open the Word
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* HERO */}
      <header className="max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-20">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="text-center md:text-left order-2 md:order-1">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono-brand font-semibold uppercase tracking-[0.2em] text-[#00E5FF] bg-[#10222A] border border-[#00E5FF]/20 rounded-full px-3 py-1 mb-6">
              Scripture First · AI-Assisted
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]">
              Open the Word.
            </h1>
            <blockquote className="text-lg sm:text-xl italic text-slate-300 mt-6 leading-relaxed max-w-xl mx-auto md:mx-0">
              "In the beginning was the Word, and the Word was with God, and the Word was God."
            </blockquote>
            <p className="text-sm font-mono-brand text-[#00E5FF] mt-3">— John 1:1 (KJV)</p>
            <p className="text-lg text-slate-400 leading-relaxed max-w-xl mx-auto md:mx-0 mt-6">
              When's the last time you actually read it — not a clip, not a sermon summary, not somebody's
              hot take? Here's the actual text. No spin. Just open it.
            </p>
            <div className="mt-8 flex justify-center md:justify-start"><Cta label="Let's get started" /></div>
          </div>
          <div className="order-1 md:order-2 relative">
            <div className="relative rounded-3xl overflow-hidden border border-[#1E273D] shadow-[0_0_60px_rgba(0,229,255,0.08)]">
              <img
                src={HERO_IMG}
                alt="Someone reading OpenWord AI on their phone in the middle of a busy day"
                className="w-full h-[420px] sm:h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-white text-sm font-medium">Wherever you are. Whatever's happening.</p>
                <p className="text-slate-300 text-xs mt-0.5">The text meets you there.</p>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl bg-[#00E5FF]/10 blur-2xl -z-10" />
          </div>
        </div>
      </header>

      {/* SECTION 1 — Why OpenWord AI Exists */}
      <section className="max-w-3xl mx-auto px-6 py-20 border-t border-[#1E273D]">
        <h2 className="text-3xl sm:text-4xl text-white leading-tight">
          Scripture was written for you.
        </h2>
        <div className="mt-6 space-y-4 text-slate-400 leading-relaxed text-lg">
          <p>Be honest — how much of what you "know" about the Bible actually came from reading it, versus
          someone else telling you what it says? A sermon clip. A relative's take at dinner. A meme. The Word
          was never meant to reach you secondhand: "All Scripture is given by inspiration of God, and is
          profitable for doctrine, for reproof, for correction, for instruction in righteousness"
          (2 Timothy 3:16, KJV) — written for the person reading it, not just the scholar explaining it.</p>
          <p>That's the whole reason OpenWord AI exists. Jesus Christ — the Living Word (John 1:14), who died,
          rose, and sits at the right hand of the Father — is who this points back to. Every verse, every
          original word you look up, every question you sit with, comes back to Him.</p>
          <p>You don't need someone's take before you've read it yourself. You need the text — unfiltered, in
          front of you, with real tools to actually understand what it says.</p>
        </div>
      </section>

      {/* SECTION 2 — How It Works */}
      <section id="how" className="max-w-3xl mx-auto px-6 py-20 border-t border-[#1E273D]">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">How It Works</div>
        <h2 className="text-3xl sm:text-4xl text-white leading-tight">Simple by design. Deep by design.</h2>
        <div className="mt-8 space-y-6">
          {[
            ["1. Come to the text", "Choose a translation and a passage — or a topic. That's it. No streaks, no badges, no algorithm deciding what you should read. Just you and the Word."],
            ["2. Choose your depth", "Beginner, intermediate, or advanced. The virtual study companion adjusts how it walks alongside you. Whether you're new to Scripture or you've been reading for decades, there is always more to discover."],
            ["3. Study with or without the virtual study companion", "The virtual study companion is there when you want a guide — asking reflection questions, helping you explore context, and pointing you back to the text. When you want silence, pause it. Read. Search. Look up original Hebrew and Greek words. Take notes. The virtual study companion waits."],
            ["4. Go Deeper when you're ready", "When something in the text catches you — a word, a phrase, a question you can't shake — Go Deeper opens a focused one-on-one conversation. The virtual study companion remembers your session and picks up where you left off."],
          ].map(([h, b]) => (
            <div key={h} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6">
              <div className="font-sans text-lg font-medium text-slate-200">{h}</div>
              <p className="text-slate-400 mt-2 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <div className="mt-10"><Cta /></div>
      </section>

      {/* SECTION 3 — What's Inside */}
      <section className="max-w-3xl mx-auto px-6 py-20 border-t border-[#1E273D]">
        <h2 className="text-3xl sm:text-4xl text-white leading-tight">
          Everything you need to engage the Word.
        </h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {[
            [BookOpen, "Multiple Translations", "Start with one, switch anytime. The text is the center."],
            [Languages, "Strong's Concordance", "Tap any word to see the original Hebrew or Greek behind it. The Concordance was compiled so that ordinary believers could access the original languages. It remains one of the most faithful study tools ever created."],
            [MessageCircle, "Virtual Study Companion", "An AI guide built to walk alongside you, not to replace the Holy Spirit, your pastor, or your church. It asks questions, offers context, and brings you back to the text. A tool, not an authority."],
            [MessageCircle, "Go Deeper", "One-on-one conversation mode for when a structured study isn't enough. Ask anything, go anywhere in the text."],
            [PauseCircle, "Study Alone Mode", "Pause the virtual study companion and study in quiet. Read, search, look things up, take notes. OpenWord AI works without the AI."],
            [NotebookPen, "Notes", "Write your own observations on any passage. Saved per passage, per session. Your words, in response to what you read."],
            [HistoryIcon, "Session Memory", "New sessions open by reflecting on where you left off, so nothing is lost."],
            [Cpu, "Choose Your Depth", "Set beginner, intermediate, or advanced in Preferences. The virtual study companion meets you where you are — no AI model to pick or configure."],
          ].map(([Icon, h, b]) => (
            <div key={h} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
              <Icon className="w-5 h-5 text-[#00E5FF] mb-3" strokeWidth={1.5} />
              <div className="font-medium text-slate-200">{h}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-8 text-center" data-testid="landing-features-pricing-teaser">
          Reading, the concordance, your notes and history are always free.{" "}
          <button onClick={() => navigate("/pricing")} data-testid="landing-features-pricing-link" className="text-[#00E5FF] underline underline-offset-2 hover:text-[#00B8CC]">
            Explore the plans
          </button>{" "}
          when you'd like more study credits to go deeper.
        </p>
      </section>

      {/* SECTION 4 — The Standard */}
      <section className="bg-[#10222A] border-y border-[#00E5FF]/10">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <h2 className="text-3xl sm:text-4xl text-white leading-tight">
            Scripture is the authority. It always has been.
          </h2>
          <Verse cite="Matthew 24:35 (KJV)">
            "Heaven and earth shall pass away, but my words shall not pass away."
          </Verse>
          <div className="mt-6 space-y-4 text-slate-300 leading-relaxed text-lg">
            <p>Quick question: when an app, an influencer, or even your own gut disagrees with the text — which
            one actually wins? OpenWord AI is built on one answer: the text does. The virtual study companion
            doesn't interpret Scripture for you or soften it to make you comfortable. It asks questions, adds
            context, hands you the original language — and then gets out of the way.</p>
            <p>It won't push you toward any denomination's spin. It asks, it points to the text, and steps back.
            What happens in that space — between you and the words on the page — is yours to work out.</p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — About */}
      <section id="about" className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">About</div>
        <div className="md:grid md:grid-cols-[1fr,220px] md:gap-8 items-start">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-tight">
              Powered by OpenWord AI.
            </h2>
            <div className="mt-6 space-y-4 text-slate-400 leading-relaxed text-lg">
              <p>Not everyone has access to a seminary. Not everyone has someone to sit down and study with. Not
              everyone even has someone who'd take their questions seriously without judging them for asking.
              That was never supposed to be a requirement. The text was always meant to speak for itself — to
              anyone willing to open it.</p>
            </div>
          </div>
          <img src={ABOUT_IMG} alt="People from every walk of life open the Word" className="hidden md:block w-full h-56 object-cover rounded-2xl border border-[#1E273D] mt-2" />
        </div>

        <div className="mt-8 rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6">
          <div className="text-sm font-medium text-slate-200 mb-4">We believe:</div>
          <ul className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-4 text-slate-400 leading-relaxed">
            {[
              "In one God, eternally existing in three persons: Father, Son, and Holy Spirit — the Trinity, co-equal and co-eternal, three-in-one.",
              "That Jesus Christ is the Son of God, fully God and fully man, who was born of a virgin, lived a sinless life, died on the cross as the atoning sacrifice for the sins of mankind, rose bodily from the dead on the third day, ascended to the right hand of the Father, and will return in glory.",
              "That the Holy Spirit dwells in every true believer, convicting the world of sin and righteousness, guiding the believer into all truth (John 16:13), and empowering the church.",
              "That the Bible — the sixty-six books of the Old and New Testaments — is the inspired, infallible, and authoritative Word of God, the final rule of faith and practice for every believer.",
              "That salvation is by grace alone, through faith alone, in Christ alone — not by works, so that no one may boast (Ephesians 2:8–9).",
              "That the church is the body of Christ, called to worship, to serve, to make disciples, and to proclaim the gospel to every nation.",
            ].map((line, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[#00E5FF] leading-relaxed">—</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 space-y-4 text-slate-400 leading-relaxed text-lg">
          <p>OpenWord AI is not a church, and it's not trying to be one. It's a study tool — built by believers,
          for anyone who wants to actually understand what they're reading, no matter where they're starting
          from. Grew up in church and stopped going? Never owned a Bible? Not sure what you believe, or sure
          you don't believe any of it? The text doesn't ask first. It just meets you where you are.</p>
        </div>
        <div className="mt-10"><Cta label="Start where you are" /></div>
      </section>

      {/* SECTION 6 — Resources */}
      <section id="resources" className="max-w-3xl mx-auto px-6 py-20 border-t border-[#1E273D]">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">Resources</div>
        <h2 className="text-3xl sm:text-4xl text-white leading-tight">Tools to support your study.</h2>
        <div className="mt-8 space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
          {[
            ["Strong's Concordance", "Search Hebrew and Greek words by number. Available inside every study session."],
            ["Translation Guide", "NIV reads naturally (licensed); WEB, KJV and ASV are public domain; NKJV and the Amplified Bible add classic and expanded readings. A fuller guide to each lives on the in-app Resources page."],
            ["How to Use OpenWord AI", "Start a study by entering a passage (like John 3) or a topic (like anxiety), then read the text slowly. From there you can look up original Hebrew and Greek words in the concordance, take notes, and tap “Go Deeper” to talk anything through with the virtual study companion. Prefer quiet? Switch to Study Alone at any time. Every study is saved automatically — revisit one or pick up where you left off from your History whenever you like."],
            ["Your Virtual Study Companion", "The virtual study companion works right out of the box — there's nothing to configure. Every plan uses the same engine; paid plans add a bigger monthly credit pool and the creation tools."],
          ].map(([h, b]) => (
            <div key={h} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
              <div className="font-medium text-slate-200">{h}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLOSE */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center border-t border-[#1E273D]">
        <h2 className="text-4xl sm:text-5xl text-white">Open the Word.</h2>
        <blockquote className="text-lg sm:text-xl italic text-slate-400 mt-8 leading-relaxed max-w-xl mx-auto">
          "Thy word is a lamp unto my feet, and a light unto my path."
        </blockquote>
        <p className="text-sm text-[#00E5FF] mt-3">— Psalm 119:105 (KJV)</p>
        <p className="text-lg text-slate-400 mt-8">
          What's actually stopping you — not the excuse, the real reason? Whatever it is, the text doesn't
          need you to have it figured out first. Whoever you are, wherever you're starting from, come and read.
        </p>
        <div className="mt-10"><Cta label="Come and read" /></div>
        <p className="text-sm text-slate-500 mt-8" data-testid="landing-pricing-teaser">
          Always free to begin.{" "}
          <button onClick={() => navigate("/pricing")} data-testid="landing-pricing-link" className="text-[#00E5FF] underline underline-offset-2 hover:text-[#00B8CC]">
            Explore the plans
          </button>{" "}
          when you're ready for more.
        </p>
      </section>

      {/* REFLECTIONS — subtle callout pointing visitors to the blog */}
      <section className="border-t border-[#1E273D] bg-[#0E131F]" data-testid="landing-reflections">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">
            <BookOpen className="w-3.5 h-3.5" strokeWidth={1.75} /> Reflections
          </div>
          <h2 className="text-2xl sm:text-3xl text-slate-200 leading-tight">
            Short readings that stay with the text.
          </h2>
          <p className="text-base text-slate-400 mt-3 leading-relaxed max-w-lg mx-auto">
            The kind of question you can't un-ask once you've read it — a word, a gap, a detail you'd walked
            past a hundred times. New pieces, no noise.
          </p>
          <button
            onClick={() => navigate("/blog")}
            data-testid="landing-reflections-link"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#00E5FF] hover:text-[#00B8CC] transition-colors"
          >
            Read the reflections <span aria-hidden>→</span>
          </button>
        </div>
      </section>

      {/* DAILY NUGGETS email signup — hidden (Daily Nuggets are now social posts, not emails).
          Kept in code (disabled) in case an email feature is revived later.
      <section className="bg-[#161D2F]" data-testid="landing-nuggets">
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-3">Daily Nuggets</div>
          <h2 className="text-3xl sm:text-4xl text-white leading-tight">
            A verse in your inbox each morning.
          </h2>
          <p className="text-base text-slate-500 mt-4 leading-relaxed max-w-lg mx-auto">
            One short passage and a reflection, every day. No account, no noise — just the Word to carry
            into your day. Leave anytime.
          </p>
          <div className="mt-8 max-w-md mx-auto">
            <NuggetSignup testid="landing-nugget-signup" />
            <button
              onClick={() => navigate("/daily")}
              data-testid="landing-nuggets-learn"
              className="text-sm text-[#00E5FF] hover:text-white transition-colors mt-4"
            >
              Learn more about Daily Nuggets
            </button>
          </div>
        </div>
      </section>
      */}

      {/* FOOTER */}
      <footer className="border-t border-[#1E273D] bg-[#0E131F]">
        <div className="max-w-5xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-center md:text-left">
          <div>
            <div className="text-lg text-slate-200">OpenWord AI</div>
            <div className="text-sm text-slate-400">Powered by OpenWord AI</div>
          </div>
          <div className="flex items-center justify-center gap-5 text-sm text-slate-500 flex-wrap">
            <button onClick={() => scrollTo("how")} className="hover:text-[#00E5FF]">How It Works</button>
            <button onClick={() => scrollTo("about")} className="hover:text-[#00E5FF]">About</button>
            <button onClick={() => scrollTo("resources")} className="hover:text-[#00E5FF]">Resources</button>
            <button onClick={() => navigate("/blog")} data-testid="footer-blog-link" className="hover:text-[#00E5FF]">Blog</button>
            <button onClick={() => navigate("/pricing")} className="hover:text-[#00E5FF]">Pricing</button>
            <button onClick={() => navigate("/settings")} className="hover:text-[#00E5FF]">Preferences</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#00E5FF]">Privacy</button>
            <button onClick={() => navigate("/terms")} className="hover:text-[#00E5FF]">Terms</button>
          </div>
          <div className="text-xs text-slate-500">© 2026 Powered by OpenWord AI</div>
        </div>
      </footer>
    </div>
  );
}
